// ===================================================================
// WORKFLOWS -- Inbox (actionable items needing the current user's attention)
// Groups by urgency: Overdue / Due Today / Upcoming
// ===================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  Card, Text, Badge, Group, Stack, Button, Loader, Center, Title,
  Paper, Modal, Textarea, TextInput, Select, Checkbox, Divider,
  ThemeIcon, Collapse,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconCircleCheck,
  IconCheck, IconX, IconPencilCheck, IconClipboardCheck, IconSignature,
  IconPlayerPlay, IconFileDescription, IconInfoCircle, IconChevronDown,
  IconChevronRight, IconFlame, IconSun, IconCalendar, IconUser,
  IconUsers, IconHistory, IconExternalLink, IconTable,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { fetchTable, TABLES, FIELDS } from '../../lib/api-switch';
import { queryRecords } from '../../api/client';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import {
  STEP_TYPE_LABELS, STEP_TYPE_COLORS, PRIORITY_COLORS,
  type WorkflowInboxItem, type WorkflowStepType,
  type WorkflowStepDef, type DocumentCheckItem, type DocumentSubmission,
} from '../../types/workflow';

// -- Urgency classification -------------------------------------------

type Urgency = 'overdue' | 'today' | 'upcoming';

function classifyUrgency(dueDate: string): Urgency {
  if (!dueDate) return 'upcoming';
  const due = new Date(dueDate).getTime();
  const now = Date.now();
  if (due < now) return 'overdue';
  if (due - now < 24 * 60 * 60 * 1000) return 'today';
  return 'upcoming';
}

function assignmentReason(
  step: WorkflowInboxItem['step'],
  userEmail: string,
  userRole: string | undefined,
): string {
  const assigneeLower = (step.assignee || '').toLowerCase();
  const emailLower = userEmail.toLowerCase();
  if (assigneeLower === emailLower) return 'Assigned directly to you';
  if (step.assigneeType === 'role') {
    if (userRole && assigneeLower === userRole.toLowerCase()) {
      return `Assigned to your role (${step.assignee})`;
    }
    return `Assigned to role: ${step.assignee}`;
  }
  if (step.assigneeType === 'supervisor') return 'Assigned via supervisor chain';
  return `Assigned to ${step.assignee}`;
}

// -- Due date label ---------------------------------------------------

function DueDateLabel({ dueDate }: { dueDate: string }) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  const diffH = diffMs / (1000 * 60 * 60);

  let color = 'dimmed';
  let prefix = 'Due ';
  if (diffMs < 0) { color = 'red'; prefix = 'Overdue -- '; }
  else if (diffH < 24) { color = 'yellow.8'; prefix = 'Due soon -- '; }

  const label = due.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  return (
    <Text size="xs" c={color} style={{ fontFamily: 'DM Mono, monospace' }}>
      {prefix}{label}
    </Text>
  );
}

// -- Inbox action card ------------------------------------------------

function InboxCard({
  item,
  userEmail,
  userRoleSlug,
  onAction,
  onViewDetail,
}: {
  item: WorkflowInboxItem;
  userEmail: string;
  userRoleSlug: string | undefined;
  onAction: (item: WorkflowInboxItem, mode: ActionMode) => void;
  onViewDetail: (item: WorkflowInboxItem) => void;
}) {
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const { step, instance, template } = item;
  const stepType = step.stepType as WorkflowStepType;
  const priority = (instance.priority || 'normal') as keyof typeof PRIORITY_COLORS;
  const stepColor = STEP_TYPE_COLORS[stepType] ?? '#64748B';
  const priorityColor = PRIORITY_COLORS[priority] ?? '#0284C7';
  const reason = assignmentReason(step, userEmail, userRoleSlug);

  // Parse instructions from template step definition
  let instructions = '';
  try {
    const defs: WorkflowStepDef[] = JSON.parse(template.stepsJson);
    const idx = Number(step.stepIndex) || 0;
    instructions = defs[idx]?.instructions ?? '';
  } catch { /* */ }

  return (
    <Card withBorder radius="sm" p="md" style={{ borderLeft: `4px solid ${stepColor}`, cursor: 'pointer' }}
      onClick={() => onViewDetail(item)}>
      <Stack gap="xs">
        {/* Header row */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <Badge
              size="xs"
              style={{ backgroundColor: stepColor, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              {STEP_TYPE_LABELS[stepType] ?? stepType}
            </Badge>
            {priority !== 'normal' && (
              <Badge
                size="xs"
                variant="outline"
                style={{ borderColor: priorityColor, color: priorityColor, textTransform: 'uppercase' }}
              >
                {priority === 'critical' && <IconFlame size={10} style={{ marginRight: 2 }} />}
                {priority}
              </Badge>
            )}
          </Group>
          <DueDateLabel dueDate={step.dueDate} />
        </Group>

        {/* Workflow context + step name */}
        <div>
          <Text fw={600} size="sm" lineClamp={1}>{step.stepName}</Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {template.name}
            {instance.sourceRecordLabel ? ` -- ${instance.sourceRecordLabel}` : ''}
          </Text>
        </div>

        {/* Assignment reason */}
        <Group gap={4}>
          {step.assigneeType === 'role'
            ? <IconUsers size={12} style={{ color: '#7C3AED' }} />
            : <IconUser size={12} style={{ color: '#64748B' }} />}
          <Text size="xs" c="dimmed">{reason}</Text>
        </Group>

        {/* Expandable instructions */}
        {instructions && (
          <div>
            <Button
              variant="subtle"
              size="xs"
              color="violet"
              p={0}
              leftSection={instructionsOpen ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
              onClick={() => setInstructionsOpen(v => !v)}
              styles={{ root: { height: 'auto' } }}
            >
              Instructions
            </Button>
            <Collapse in={instructionsOpen}>
              <Paper p="xs" radius="sm" mt={4} withBorder style={{ borderColor: '#7C3AED33', backgroundColor: '#F5F3FF' }}>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{instructions}</Text>
              </Paper>
            </Collapse>
          </div>
        )}

        {/* Action buttons */}
        <Group gap="xs" mt={4} wrap="wrap" onClick={e => e.stopPropagation()}>
          {stepType === 'approval' && (
            <>
              <Button size="xs" color="green" leftSection={<IconCheck size={13} />} onClick={() => onAction(item, 'approve')}>
                Approve
              </Button>
              <Button size="xs" color="red" variant="light" leftSection={<IconX size={13} />} onClick={() => onAction(item, 'reject')}>
                Reject
              </Button>
            </>
          )}
          {stepType === 'review' && (
            <Button size="xs" style={{ backgroundColor: STEP_TYPE_COLORS.review, color: '#fff' }} leftSection={<IconPencilCheck size={13} />} onClick={() => onAction(item, 'complete')}>
              Mark Reviewed
            </Button>
          )}
          {stepType === 'task' && (
            <Button size="xs" style={{ backgroundColor: STEP_TYPE_COLORS.task, color: '#fff' }} leftSection={<IconClipboardCheck size={13} />} onClick={() => onAction(item, 'complete')}>
              Mark Complete
            </Button>
          )}
          {stepType === 'signature' && (
            <Button size="xs" style={{ backgroundColor: STEP_TYPE_COLORS.signature, color: '#fff' }} leftSection={<IconSignature size={13} />} onClick={() => onAction(item, 'sign')}>
              Sign
            </Button>
          )}
          {stepType === 'notification' && (
            <Button size="xs" style={{ backgroundColor: STEP_TYPE_COLORS.notification, color: '#fff' }} leftSection={<IconCheck size={13} />} onClick={() => onAction(item, 'complete')}>
              Acknowledge
            </Button>
          )}
          <Button size="xs" variant="subtle" color="gray" leftSection={<IconExternalLink size={13} />} onClick={() => onViewDetail(item)}>
            View Details
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// -- Action modal -----------------------------------------------------

type ActionMode = 'approve' | 'reject' | 'complete' | 'sign';

interface ModalState {
  item: WorkflowInboxItem;
  mode: ActionMode;
}

function ActionModal({
  modalState,
  onClose,
  submitting,
  onSubmit,
}: {
  modalState: ModalState | null;
  onClose: () => void;
  submitting: boolean;
  onSubmit: (comments: string, signatureData?: string, docSubmissions?: DocumentSubmission[]) => void;
}) {
  const [comments, setComments] = useState('');
  const [sigName, setSigName] = useState('');
  const [docChecks, setDocChecks] = useState<Record<string, boolean>>({});

  const stepDef = useMemo(() => {
    if (!modalState) return null;
    try {
      const defs: WorkflowStepDef[] = JSON.parse(modalState.item.template.stepsJson);
      return defs[Number(modalState.item.step.stepIndex) || 0] ?? null;
    } catch { return null; }
  }, [modalState]);

  const docChecklist: DocumentCheckItem[] = stepDef?.documentChecklist ?? [];
  const instructions = stepDef?.instructions ?? '';
  const requiredDocs = docChecklist.filter(d => d.required);
  const allRequiredChecked = requiredDocs.every(d => docChecks[d.key]);

  useEffect(() => {
    if (modalState) { setComments(''); setSigName(''); setDocChecks({}); }
  }, [modalState]);

  if (!modalState) return null;
  const { item, mode } = modalState;
  const { step, instance, template } = item;

  const titles: Record<ActionMode, string> = {
    approve: 'Approve Step',
    reject: 'Reject Step',
    complete: step.stepType === 'notification' ? 'Acknowledge Notification'
      : step.stepType === 'review' ? 'Confirm Review' : 'Mark Complete',
    sign: 'Sign Step',
  };

  // Notification message from step config
  const notificationMessage = stepDef?.config?.message || stepDef?.config?.messageTemplate || '';

  const commentsRequired = mode === 'reject';
  const canSubmit = !submitting &&
    (!commentsRequired || comments.trim().length > 0) &&
    (mode !== 'sign' || sigName.trim().length > 0) &&
    (mode === 'reject' || allRequiredChecked);

  function handleSubmit() {
    const docSubs: DocumentSubmission[] | undefined = docChecklist.length > 0
      ? docChecklist.map(d => ({ key: d.key, completed: !!docChecks[d.key], completedAt: docChecks[d.key] ? new Date().toISOString() : undefined }))
      : undefined;
    onSubmit(comments, mode === 'sign' ? sigName : undefined, docSubs);
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={<Group gap="xs"><ThemeIcon size="sm" style={{ backgroundColor: '#7C3AED' }} radius="sm">
        {mode === 'approve' ? <IconCheck size={14} /> : mode === 'reject' ? <IconX size={14} /> : mode === 'sign' ? <IconSignature size={14} /> : <IconClipboardCheck size={14} />}
      </ThemeIcon><Text fw={600} size="sm">{titles[mode]}</Text></Group>}
      size="lg"
    >
      <Stack gap="md">
        <Paper p="sm" radius="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
          <Text size="xs" c="dimmed" mb={2}>Workflow</Text>
          <Group gap="xs" mb={6}>
            <Text size="sm" fw={600}>{template.name}</Text>
            {template.module && template.module !== '*' && (
              <Badge size="xs" variant="outline">{template.module}</Badge>
            )}
            {instance.priority && instance.priority !== 'normal' && (
              <Badge size="xs" color={PRIORITY_COLORS[instance.priority as keyof typeof PRIORITY_COLORS] ?? '#64748B'}>
                {instance.priority}
              </Badge>
            )}
          </Group>
          <Divider my={6} />
          <Group justify="space-between" wrap="nowrap">
            <div>
              <Text size="xs" c="dimmed">Current Step</Text>
              <Group gap="xs">
                <Badge size="xs" color={STEP_TYPE_COLORS[step.stepType as WorkflowStepType] ?? '#64748B'} variant="light">
                  {STEP_TYPE_LABELS[step.stepType as WorkflowStepType] ?? step.stepType}
                </Badge>
                <Text size="sm" fw={500}>{step.stepName}</Text>
              </Group>
              <Text size="xs" c="dimmed" mt={2}>
                Step {Number(step.stepIndex) + 1} of {instance.totalSteps}
              </Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text size="xs" c="dimmed">Assigned to</Text>
              <Text size="sm">{step.assigneeName || step.assignee}</Text>
              <Text size="xs" c="dimmed" mt={2}>Started by {instance.startedBy}</Text>
            </div>
          </Group>
          {instance.sourceRecordLabel && (
            <>
              <Divider my={6} />
              <Text size="xs" c="dimmed">Record</Text>
              <Text size="sm" fw={500}>{instance.sourceRecordLabel}</Text>
              {instance.sourceRecordId && (
                <Text size="xs" c="dimmed">ID: {instance.sourceRecordId}</Text>
              )}
            </>
          )}
          {instance.startedAt && (
            <>
              <Divider my={6} />
              <Text size="xs" c="dimmed">
                Workflow started {new Date(instance.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </Text>
            </>
          )}
        </Paper>

        {notificationMessage && (
          <Paper p="sm" radius="sm" withBorder style={{ borderColor: '#6366F133', backgroundColor: '#EEF2FF' }}>
            <Group gap={6} mb={4}>
              <IconInfoCircle size={14} style={{ color: '#6366F1' }} />
              <Text size="xs" fw={600} style={{ color: '#6366F1' }}>Message</Text>
            </Group>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{notificationMessage}</Text>
          </Paper>
        )}

        {instructions && (
          <Paper p="sm" radius="sm" withBorder style={{ borderColor: '#7C3AED33', backgroundColor: '#F5F3FF' }}>
            <Group gap={6} mb={4}>
              <IconInfoCircle size={14} style={{ color: '#7C3AED' }} />
              <Text size="xs" fw={600} style={{ color: '#7C3AED' }}>Instructions</Text>
            </Group>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{instructions}</Text>
          </Paper>
        )}

        {docChecklist.length > 0 && mode !== 'reject' && (
          <div>
            <Group gap={6} mb={8}>
              <IconFileDescription size={14} style={{ color: '#0284C7' }} />
              <Text size="xs" fw={600} c="blue">
                Document Checklist ({Object.values(docChecks).filter(Boolean).length}/{docChecklist.length})
              </Text>
            </Group>
            <Stack gap={6}>
              {docChecklist.map(doc => (
                <Paper key={doc.key} p="xs" radius="sm" withBorder
                  style={{ borderColor: docChecks[doc.key] ? '#05966933' : doc.required ? '#DC262622' : '#e5e7eb' }}>
                  <Checkbox size="sm" color="green"
                    label={<Group gap={4}><Text size="sm">{doc.label}</Text>{doc.required && <Badge size="xs" color="red" variant="light">Required</Badge>}</Group>}
                    description={doc.description}
                    checked={!!docChecks[doc.key]}
                    onChange={e => setDocChecks(prev => ({ ...prev, [doc.key]: e.currentTarget.checked }))}
                  />
                </Paper>
              ))}
            </Stack>
            {requiredDocs.length > 0 && !allRequiredChecked && (
              <Text size="xs" c="red" mt={4}>All required documents must be checked before submitting.</Text>
            )}
          </div>
        )}

        {mode === 'sign' && (
          <div>
            <Text size="sm" fw={500} mb={6}>Type your full name to sign</Text>
            <input
              value={sigName}
              onChange={e => setSigName(e.target.value)}
              placeholder="Full legal name"
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6,
                border: '1px solid #ced4da', fontFamily: 'DM Mono, monospace',
                fontSize: 15, outline: 'none',
              }}
            />
          </div>
        )}

        <Textarea
          label={commentsRequired ? 'Reason for rejection (required)' : 'Comments (optional)'}
          placeholder={mode === 'reject' ? 'Explain why this is being rejected...' : 'Add notes...'}
          minRows={3} value={comments}
          onChange={e => setComments(e.target.value)}
          required={commentsRequired}
        />

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            color={mode === 'reject' ? 'red' : mode === 'approve' ? 'green' : '#7C3AED'}
            onClick={handleSubmit} disabled={!canSubmit} loading={submitting}
          >
            {mode === 'approve' ? 'Approve' : mode === 'reject' ? 'Reject' : mode === 'sign' ? 'Submit Signature' : step.stepType === 'notification' ? 'Acknowledge' : step.stepType === 'review' ? 'Confirm Review' : 'Mark Complete'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// -- Urgency section --------------------------------------------------

const URGENCY_CONFIG: Record<Urgency, { label: string; color: string; icon: React.ReactNode }> = {
  overdue: { label: 'Overdue', color: '#DC2626', icon: <IconFlame size={16} /> },
  today: { label: 'Due Today', color: '#D97706', icon: <IconSun size={16} /> },
  upcoming: { label: 'Upcoming', color: '#64748B', icon: <IconCalendar size={16} /> },
};

function UrgencySection({
  urgency,
  items,
  userEmail,
  userRoleSlug,
  onAction,
  onViewDetail,
}: {
  urgency: Urgency;
  items: WorkflowInboxItem[];
  userEmail: string;
  userRoleSlug: string | undefined;
  onAction: (item: WorkflowInboxItem, mode: ActionMode) => void;
  onViewDetail: (item: WorkflowInboxItem) => void;
}) {
  const cfg = URGENCY_CONFIG[urgency];
  if (items.length === 0) return null;

  return (
    <div>
      <Group gap="xs" mb="xs">
        <ThemeIcon size="sm" variant="light" style={{ backgroundColor: `${cfg.color}18`, color: cfg.color }} radius="sm">
          {cfg.icon}
        </ThemeIcon>
        <Text size="sm" fw={600} style={{ color: cfg.color }}>
          {cfg.label}
        </Text>
        <Badge size="xs" style={{ backgroundColor: cfg.color, color: '#fff' }}>
          {items.length}
        </Badge>
      </Group>
      <Stack gap="sm">
        {items.map(item => (
          <InboxCard key={item.step.Rid} item={item} userEmail={userEmail} userRoleSlug={userRoleSlug} onAction={onAction} onViewDetail={onViewDetail} />
        ))}
      </Stack>
    </div>
  );
}

// -- Main component ---------------------------------------------------

export default function WorkflowInbox() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { myRole, demoRoleOverride } = useRoleStore();
  const { loading, templates, instances, steps, loadAll, completeStep, rejectStep, startWorkflow } = useWorkflowStore();

  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [startTemplateId, setStartTemplateId] = useState<string | null>(null);
  const [startRecordLabel, setStartRecordLabel] = useState('');
  const [startRecordId, setStartRecordId] = useState('');
  const [starting, setStarting] = useState(false);

  // Detail modal
  const [detailItem, setDetailItem] = useState<WorkflowInboxItem | null>(null);
  const [sourceRecord, setSourceRecord] = useState<Record<string, unknown> | null>(null);
  const [fieldLabelMap, setFieldLabelMap] = useState<Record<string, string>>({});
  const [lookupMap, setLookupMap] = useState<Record<string, string>>({});
  const [loadingRecord, setLoadingRecord] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const userEmail = user?.email ?? '';
  // Effective role: demo override > assigned role > raw GAB auth role (fallback when userRoles table is empty)
  const effectiveRole = demoRoleOverride ?? myRole;
  const userRoleSlug = effectiveRole?.roleSlug ?? user?.role ?? '';

  // Build inbox items with role matching
  const inboxItems = useMemo(() => {
    const activeSteps = steps.filter(st => {
      if (st.status !== 'pending' && st.status !== 'in_progress') return false;
      const roleLower = (st.assignedRole || '').toLowerCase();
      const assigneeLower = (st.assignee || '').toLowerCase();
      // 'all' wildcard — everyone sees it
      if (roleLower === 'all' || assigneeLower === 'all') return true;
      // Direct email match
      if (userEmail && assigneeLower === userEmail.toLowerCase()) return true;
      // Role slug match — check assignedRole and assignee against all known role identifiers
      if (st.assignedRole || st.assigneeType === 'role') {
        const roleToMatch = roleLower || assigneeLower;
        if (!roleToMatch) return false;
        if (userRoleSlug && roleToMatch === userRoleSlug.toLowerCase()) return true;
        if (effectiveRole?.roleName && roleToMatch === effectiveRole.roleName.toLowerCase()) return true;
      }
      return false;
    });

    const items: WorkflowInboxItem[] = [];
    for (const step of activeSteps) {
      const instance = instances.find(inst => String(inst.Rid) === step.instanceId);
      if (!instance || instance.status !== 'active') continue;
      const template = templates.find(t => String(t.Rid) === String(instance.templateId));
      if (!template) continue;
      items.push({ step, instance: instance as any, template: template as any });
    }
    return items;
  }, [steps, instances, templates, userEmail, userRoleSlug, effectiveRole?.roleName]);

  // Group by urgency
  const grouped = useMemo(() => {
    const overdue: WorkflowInboxItem[] = [];
    const today: WorkflowInboxItem[] = [];
    const upcoming: WorkflowInboxItem[] = [];
    for (const item of inboxItems) {
      const u = classifyUrgency(item.step.dueDate);
      if (u === 'overdue') overdue.push(item);
      else if (u === 'today') today.push(item);
      else upcoming.push(item);
    }
    // Sort overdue by most overdue first, today by soonest first
    const byDue = (a: WorkflowInboxItem, b: WorkflowInboxItem) => {
      const da = a.step.dueDate ? new Date(a.step.dueDate).getTime() : Infinity;
      const db = b.step.dueDate ? new Date(b.step.dueDate).getTime() : Infinity;
      return da - db;
    };
    overdue.sort(byDue);
    today.sort(byDue);
    return { overdue, today, upcoming };
  }, [inboxItems]);

  // Recently completed items (last 7 days, by this user)
  const [showHistory, setShowHistory] = useState(false);
  const recentlyCompleted = useMemo(() => {
    if (!userEmail) return [];
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return steps.filter(st => {
      if (st.status !== 'completed' && st.status !== 'rejected') return false;
      if (st.completedBy?.toLowerCase() !== userEmail.toLowerCase()) return false;
      const completedTime = st.completedAt ? new Date(st.completedAt).getTime() : 0;
      return completedTime > sevenDaysAgo;
    }).map(st => {
      const instance = instances.find(inst => String(inst.Rid) === st.instanceId);
      return { step: st, instance };
    }).filter(x => x.instance).sort((a, b) => {
      const ta = a.step.completedAt ? new Date(a.step.completedAt).getTime() : 0;
      const tb = b.step.completedAt ? new Date(b.step.completedAt).getTime() : 0;
      return tb - ta; // most recent first
    });
  }, [steps, instances, userEmail]);

  // -- Detail modal ---------------------------------------------------

  async function openDetail(item: WorkflowInboxItem) {
    setDetailItem(item);
    setSourceRecord(null);
    setFieldLabelMap({});


    const { sourceRecordId, sourceTable, sourceModule } = item.instance;

    if (sourceRecordId && sourceModule === 'forms') {
      // Form response — fetch response AND the form template schema for field labels
      setLoadingRecord(true);
      try {
        const FFR = FIELDS.formResponses;
        const FFT = FIELDS.formTemplates;
        const [responseResult, templateResult] = await Promise.all([
          queryRecords<Record<string, unknown>>(TABLES.formResponses, {
            filters: [{ field: 'record_id', operator: 'eq', value: sourceRecordId }],
            limit: 5,
          }).catch(() => queryRecords<Record<string, unknown>>(TABLES.formResponses, { limit: 100 })),
          queryRecords<Record<string, unknown>>(TABLES.formTemplates, { limit: 200 }),
        ]);
        const responseRows = responseResult.records ?? [];
        const templateRows = templateResult.records ?? [];
        const match = responseRows.find((r: Record<string, unknown>) => String(r.record_id) === sourceRecordId || String(r.id) === sourceRecordId);
        if (match) {
          setSourceRecord(match as Record<string, unknown>);
          // Find the form template and build key→label map from schema
          const templateId = (match as Record<string, unknown>)[FFR.templateId];
          const formTemplate = templateRows.find((t: Record<string, unknown>) => String(t.record_id) === String(templateId) || String(t.id) === String(templateId));
          if (formTemplate) {
            const schemaStr = (formTemplate as Record<string, unknown>)[FFT.schema] as string;
            if (schemaStr) {
              try {
                const schema = JSON.parse(schemaStr);
                const labels: Record<string, string> = {};
                for (const page of (schema.pages || [])) {
                  for (const field of (page.fields || [])) {
                    if (field.key && field.label) labels[field.key] = field.label;
                  }
                }
                setFieldLabelMap(labels);
              } catch { /* */ }
            }
          }
        }
      } catch { /* ignore */ }
      setLoadingRecord(false);
    } else if (sourceRecordId && sourceTable) {
      // Other module — fetch source record + resolve foreign key lookups
      setLoadingRecord(true);
      try {
        const [rows, shelterRows, incidentRows] = await Promise.all([
          fetchTable(sourceTable, { limit: 200 }),
          fetchTable(TABLES.shelters, { limit: 200 }).catch(() => []),
          fetchTable(TABLES.incidents, { limit: 200 }).catch(() => []),
        ]);
        const match = rows.find((r: Record<string, unknown>) => String(r.record_id) === sourceRecordId || String(r.id) === sourceRecordId);
        if (match) setSourceRecord(match as Record<string, unknown>);

        // Build lookup map for foreign keys
        const FS = FIELDS.shelters;
        const FI = FIELDS.incidents;
        const lm: Record<string, string> = {};
        for (const s of shelterRows as Record<string, unknown>[]) {
          const sid = String(s.record_id || s.id || '');
          lm[`shelter:${sid}`] = String(s[FS.name] ?? '') || `Shelter ${sid}`;
        }
        for (const i of incidentRows as Record<string, unknown>[]) {
          const iid = String(i.record_id || i.id || '');
          lm[`incident:${iid}`] = String(i[FI.name] ?? '') || `Incident ${iid}`;
        }
        setLookupMap(lm);
      } catch { /* ignore */ }
      setLoadingRecord(false);
    }
  }

  // -- Action handler -------------------------------------------------

  function openAction(item: WorkflowInboxItem, mode: ActionMode) {
    setModalState({ item, mode });
  }

  async function handleSubmit(comments: string, signatureData?: string, docSubmissions?: DocumentSubmission[]) {
    if (!modalState || !user) return;
    const { item, mode } = modalState;
    const { step } = item;
    setSubmitting(true);
    try {
      let fullComments = comments || '';
      if (docSubmissions && docSubmissions.length > 0) {
        const docSummary = docSubmissions.map(d => `${d.completed ? '[x]' : '[ ]'} ${d.key}`).join('\n');
        fullComments = fullComments
          ? `${fullComments}\n\n--- Document Checklist ---\n${docSummary}`
          : `--- Document Checklist ---\n${docSummary}`;
      }

      if (mode === 'reject') {
        await rejectStep(step.Rid, user.email, fullComments || undefined);
        notifications.show({ message: 'Step rejected', color: 'red' });
      } else {
        const outcome = mode === 'approve' ? 'approved' : mode === 'sign' ? 'signed' : 'completed';
        await completeStep(step.Rid, outcome, user.email, [fullComments, signatureData].filter(Boolean).join('\n') || undefined);
        notifications.show({
          message: mode === 'approve' ? 'Step approved' : mode === 'sign' ? 'Signature submitted' : 'Step completed',
          color: 'green',
        });
      }
      setModalState(null);
    } catch (err) {
      notifications.show({ message: `Action failed: ${err}`, color: 'red' });
    } finally {
      setSubmitting(false);
    }
  }

  // -- Start workflow -------------------------------------------------

  const activeTemplates = useMemo(
    () => templates.filter(t =>
      (t.active === 'yes' || t.active === 'Yes') &&
      (t.triggerType === 'manual' || t.triggerType === '' || !t.triggerType)
    ),
    [templates],
  );
  const selectedTemplate = activeTemplates.find(t => String(t.Rid) === startTemplateId);

  async function handleStartWorkflow() {
    if (!selectedTemplate || !user) return;
    setStarting(true);
    try {
      await startWorkflow(
        selectedTemplate.Rid,
        selectedTemplate.module || '*',
        startRecordId || '',
        startRecordLabel || selectedTemplate.name,
        user.email,
      );
      notifications.show({ message: 'Workflow started', color: 'green' });
      setStartOpen(false);
      setStartTemplateId(null);
      setStartRecordLabel('');
      setStartRecordId('');
    } catch (err) {
      notifications.show({ message: `Failed to start: ${err}`, color: 'red' });
    } finally {
      setStarting(false);
    }
  }

  // -- Render ---------------------------------------------------------

  const totalPending = inboxItems.length;

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Group gap="xs">
          <Title order={3} c="black">My Inbox</Title>
          {totalPending > 0 && (
            <Badge size="lg" style={{ backgroundColor: '#7C3AED', color: '#fff' }}>
              {totalPending}
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          {loading && <Loader size="xs" color="#7C3AED" />}
          <Button size="xs" leftSection={<IconPlayerPlay size={14} />} style={{ backgroundColor: '#7C3AED' }} onClick={() => setStartOpen(true)}>
            Start Workflow
          </Button>
        </Group>
      </Group>

      {/* Content */}
      {loading && inboxItems.length === 0 ? (
        <Center py={60}>
          <Stack align="center" gap="sm">
            <Loader color="#7C3AED" />
            <Text size="sm" c="dimmed">Loading your inbox...</Text>
          </Stack>
        </Center>
      ) : totalPending === 0 ? (
        <Center py={60}>
          <Stack align="center" gap="sm">
            <ThemeIcon size={56} radius="xl" style={{ backgroundColor: '#F3F0FF' }}>
              <IconCircleCheck size={28} style={{ color: '#7C3AED' }} />
            </ThemeIcon>
            <Text fw={600} size="md">All caught up!</Text>
            <Text size="sm" c="dimmed">No items awaiting your action.</Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="lg">
          <UrgencySection urgency="overdue" items={grouped.overdue} userEmail={userEmail} userRoleSlug={userRoleSlug} onAction={openAction} onViewDetail={openDetail} />
          <UrgencySection urgency="today" items={grouped.today} userEmail={userEmail} userRoleSlug={userRoleSlug} onAction={openAction} onViewDetail={openDetail} />
          <UrgencySection urgency="upcoming" items={grouped.upcoming} userEmail={userEmail} userRoleSlug={userRoleSlug} onAction={openAction} onViewDetail={openDetail} />
        </Stack>
      )}

      {/* Recently completed */}
      {recentlyCompleted.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Button
            variant="subtle" color="gray" size="xs"
            leftSection={<IconHistory size={14} />}
            rightSection={showHistory ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
            onClick={() => setShowHistory(!showHistory)}
          >
            Recently Completed ({recentlyCompleted.length})
          </Button>
          <Collapse in={showHistory}>
            <Stack gap="xs" mt="xs">
              {recentlyCompleted.map(({ step: st, instance: inst }) => (
                <Paper key={st.Rid || st.id} withBorder p="xs" radius="sm" style={{ opacity: 0.7, cursor: 'pointer' }}
                  onClick={() => inst && navigate(`/workflows/instance/${inst.Rid}`)}>
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap">
                      <Badge size="xs" color={STEP_TYPE_COLORS[st.stepType as WorkflowStepType] ?? '#64748B'} variant="light">
                        {STEP_TYPE_LABELS[st.stepType as WorkflowStepType] ?? st.stepType}
                      </Badge>
                      <Text size="xs" fw={500}>{st.stepName}</Text>
                      <Text size="xs" c="dimmed">— {inst?.templateName}</Text>
                      {inst?.sourceRecordLabel && <Text size="xs" c="dimmed">({inst.sourceRecordLabel})</Text>}
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <Badge size="xs" variant="light" color={st.status === 'completed' ? 'green' : 'red'}>
                        {st.outcome || st.status}
                      </Badge>
                      {st.completedAt && (
                        <Text size="xs" c="dimmed">
                          {new Date(st.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </Text>
                      )}
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Collapse>
        </div>
      )}

      {/* Detail modal */}
      <Modal
        opened={!!detailItem}
        onClose={() => setDetailItem(null)}
        title={<Group gap="xs"><IconTable size={16} style={{ color: '#7C3AED' }} /><Text fw={600} size="sm">Workflow Item Details</Text></Group>}
        size="lg"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {detailItem && (() => {
          const { step, instance, template } = detailItem;
          const stepType = step.stepType as WorkflowStepType;
          let stepDef: WorkflowStepDef | null = null;
          try {
            const defs: WorkflowStepDef[] = JSON.parse(template.stepsJson);
            stepDef = defs[Number(step.stepIndex) || 0] ?? null;
          } catch { /* */ }
          const notifMsg = stepDef?.config?.message || stepDef?.config?.messageTemplate || '';

          // Parse form response data if available
          let responseFields: [string, string][] = [];
          if (sourceRecord) {
            const rd = (sourceRecord.responseData ?? sourceRecord.response_data) as string | undefined;
            if (rd) {
              try {
                const parsed = JSON.parse(rd) as Record<string, unknown>;
                responseFields = Object.entries(parsed)
                  .filter(([, v]) => v !== null && v !== undefined && v !== '')
                  .map(([k, v]) => [fieldLabelMap[k] || k, typeof v === 'object' ? JSON.stringify(v) : String(v)]);
              } catch { /* */ }
            }
            // If no responseData (not a form), prefer contextJson (pre-formatted) over raw record
            if (responseFields.length === 0) {
              let useContext = false;
              if (instance.contextJson) {
                try {
                  const ctx = JSON.parse(instance.contextJson) as Record<string, unknown>;
                  const ctxEntries = Object.entries(ctx).filter(([, v]) => v !== null && v !== undefined && v !== '');
                  if (ctxEntries.length > 0) {
                    const friendlyKey = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
                    responseFields = ctxEntries.map(([k, v]) => [friendlyKey(k), typeof v === 'object' ? JSON.stringify(v) : String(v)]);
                    useContext = true;
                  }
                } catch {
                  // JSON.parse failed — Ignatius may have stripped escapes from nested JSON.
                  // Try to extract simple key:value pairs using regex as a fallback.
                  try {
                    const friendlyKey = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
                    // Match "key":"value" pairs that have simple string values (not nested objects)
                    const pairs = [...instance.contextJson.matchAll(/"([a-zA-Z]+)":"([^"]*?)"/g)];
                    if (pairs.length > 3) {
                      const skipKeys = new Set(['id', 'Rid']);
                      responseFields = pairs
                        .filter(m => !skipKeys.has(m[1]) && m[2] !== '')
                        .map(m => [friendlyKey(m[1]), m[2]]);
                      useContext = true;
                    }
                  } catch { /* truly broken — fall through */ }
                }
              }
              if (!useContext) {
                const skipKeys = new Set(['id', 'datecreated', 'datemodified', 'createdby', 'modifiedby']);
                const friendlyKey = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
                const resolveValue = (k: string, v: unknown): string => {
                  const str = typeof v === 'object' ? JSON.stringify(v) : String(v);
                  if (k.toLowerCase().includes('shelterid') && lookupMap[`shelter:${str}`]) return lookupMap[`shelter:${str}`];
                  if (k.toLowerCase().includes('incidentid') && lookupMap[`incident:${str}`]) return lookupMap[`incident:${str}`];
                  return str;
                };
                responseFields = Object.entries(sourceRecord)
                  .filter(([k, v]) => v !== null && v !== undefined && v !== '' && !skipKeys.has(k))
                  .map(([k, v]) => [friendlyKey(k), resolveValue(k, v)]);
              }
            }
          }

          return (
            <Stack gap="md">
              {/* Workflow context */}
              <Paper p="sm" radius="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Group justify="space-between" mb={6}>
                  <div>
                    <Text size="xs" c="dimmed">Workflow</Text>
                    <Text size="sm" fw={600}>{template.name}</Text>
                  </div>
                  <Group gap="xs">
                    {template.module && template.module !== '*' && <Badge size="xs" variant="outline">{template.module}</Badge>}
                    {instance.priority && instance.priority !== 'normal' && (
                      <Badge size="xs" color={PRIORITY_COLORS[instance.priority as keyof typeof PRIORITY_COLORS] ?? '#64748B'}>{instance.priority}</Badge>
                    )}
                  </Group>
                </Group>
                <Divider my={6} />
                <Group justify="space-between">
                  <div>
                    <Text size="xs" c="dimmed">Current Step</Text>
                    <Group gap="xs">
                      <Badge size="xs" color={STEP_TYPE_COLORS[stepType] ?? '#64748B'} variant="light">
                        {STEP_TYPE_LABELS[stepType] ?? stepType}
                      </Badge>
                      <Text size="sm" fw={500}>{step.stepName}</Text>
                    </Group>
                    <Text size="xs" c="dimmed" mt={2}>Step {Number(step.stepIndex) + 1} of {instance.totalSteps}</Text>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Text size="xs" c="dimmed">Assigned to</Text>
                    <Text size="sm">{step.assigneeName || step.assignee}</Text>
                    <Text size="xs" c="dimmed" mt={2}>Started by {instance.startedBy}</Text>
                  </div>
                </Group>
                {instance.sourceRecordLabel && (
                  <>
                    <Divider my={6} />
                    <Text size="xs" c="dimmed">Record</Text>
                    <Text size="sm" fw={500}>{instance.sourceRecordLabel}</Text>
                    {instance.sourceRecordId && <Text size="xs" c="dimmed">ID: {instance.sourceRecordId}</Text>}
                  </>
                )}
                {instance.startedAt && (
                  <>
                    <Divider my={6} />
                    <Text size="xs" c="dimmed">
                      Started {new Date(instance.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </Text>
                  </>
                )}
              </Paper>

              {/* Notification message */}
              {notifMsg && (
                <Paper p="sm" radius="sm" withBorder style={{ borderColor: '#6366F133', backgroundColor: '#EEF2FF' }}>
                  <Group gap={6} mb={4}>
                    <IconInfoCircle size={14} style={{ color: '#6366F1' }} />
                    <Text size="xs" fw={600} style={{ color: '#6366F1' }}>Message</Text>
                  </Group>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{notifMsg}</Text>
                </Paper>
              )}

              {/* Instructions */}
              {stepDef?.instructions && (
                <Paper p="sm" radius="sm" withBorder style={{ borderColor: '#7C3AED33', backgroundColor: '#F5F3FF' }}>
                  <Group gap={6} mb={4}>
                    <IconInfoCircle size={14} style={{ color: '#7C3AED' }} />
                    <Text size="xs" fw={600} style={{ color: '#7C3AED' }}>Instructions</Text>
                  </Group>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{stepDef.instructions}</Text>
                </Paper>
              )}

              {/* Source record data */}
              {loadingRecord && <Center py="md"><Loader size="sm" color="#7C3AED" /></Center>}
              {responseFields.length > 0 && (
                <Paper p="sm" radius="sm" withBorder>
                  <Group gap={6} mb={8}>
                    <IconFileDescription size={14} style={{ color: '#0284C7' }} />
                    <Text size="xs" fw={600} c="blue">
                      {instance.sourceModule === 'forms' ? 'Form Response Data' : 'Source Record Data'}
                    </Text>
                  </Group>
                  <Stack gap={4}>
                    {responseFields.map(([key, val]) => (
                      <Group key={key} gap="xs" wrap="nowrap" align="flex-start">
                        <Text size="xs" c="dimmed" fw={600} style={{ minWidth: 120, flexShrink: 0 }}>{key}</Text>
                        <Text size="xs" style={{ wordBreak: 'break-word' }}>{val}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              )}
              {!loadingRecord && responseFields.length === 0 && instance.sourceRecordId && (
                <Text size="xs" c="dimmed" ta="center">No source record data available.</Text>
              )}

              {/* Link to instance detail */}
              <Button
                variant="light" color="#7C3AED" size="xs"
                leftSection={<IconExternalLink size={14} />}
                onClick={() => { setDetailItem(null); navigate(`/workflows/instance/${instance.Rid}`); }}
              >
                View Full Workflow Timeline
              </Button>

              <Divider />

              {/* Action buttons */}
              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" color="gray" onClick={() => setDetailItem(null)}>Close</Button>
                {stepType === 'approval' && (
                  <>
                    <Button color="red" variant="light" leftSection={<IconX size={14} />}
                      onClick={() => { setDetailItem(null); openAction(detailItem, 'reject'); }}>
                      Reject
                    </Button>
                    <Button color="green" leftSection={<IconCheck size={14} />}
                      onClick={() => { setDetailItem(null); openAction(detailItem, 'approve'); }}>
                      Approve
                    </Button>
                  </>
                )}
                {stepType === 'review' && (
                  <Button style={{ backgroundColor: STEP_TYPE_COLORS.review, color: '#fff' }} leftSection={<IconPencilCheck size={14} />}
                    onClick={() => { setDetailItem(null); openAction(detailItem, 'complete'); }}>
                    Mark Reviewed
                  </Button>
                )}
                {stepType === 'task' && (
                  <Button style={{ backgroundColor: STEP_TYPE_COLORS.task, color: '#fff' }} leftSection={<IconClipboardCheck size={14} />}
                    onClick={() => { setDetailItem(null); openAction(detailItem, 'complete'); }}>
                    Mark Complete
                  </Button>
                )}
                {stepType === 'signature' && (
                  <Button style={{ backgroundColor: STEP_TYPE_COLORS.signature, color: '#fff' }} leftSection={<IconSignature size={14} />}
                    onClick={() => { setDetailItem(null); openAction(detailItem, 'sign'); }}>
                    Sign
                  </Button>
                )}
                {stepType === 'notification' && (
                  <Button style={{ backgroundColor: STEP_TYPE_COLORS.notification, color: '#fff' }} leftSection={<IconCheck size={14} />}
                    onClick={() => { setDetailItem(null); openAction(detailItem, 'complete'); }}>
                    Acknowledge
                  </Button>
                )}
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* Action modal */}
      <ActionModal modalState={modalState} onClose={() => setModalState(null)} submitting={submitting} onSubmit={handleSubmit} />

      {/* Start workflow modal */}
      <Modal
        opened={startOpen}
        onClose={() => setStartOpen(false)}
        title={<Group gap="xs"><ThemeIcon size="sm" style={{ backgroundColor: '#7C3AED' }} radius="sm"><IconPlayerPlay size={14} /></ThemeIcon><Text fw={600} size="sm">Start Workflow</Text></Group>}
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Workflow Template"
            placeholder="Select a workflow to start..."
            data={activeTemplates.map(t => ({ value: String(t.Rid), label: `${t.name}${t.module && t.module !== '*' ? ` (${t.module})` : ''}` }))}
            value={startTemplateId}
            onChange={setStartTemplateId}
            searchable
          />
          {selectedTemplate && (
            <>
              {selectedTemplate.description && (
                <Paper p="sm" radius="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                  <Text size="xs" c="dimmed">Description</Text>
                  <Text size="sm">{selectedTemplate.description}</Text>
                </Paper>
              )}
              {selectedTemplate.stepsJson && (() => {
                try {
                  const defs: WorkflowStepDef[] = JSON.parse(selectedTemplate.stepsJson);
                  return (
                    <Paper p="sm" radius="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                      <Text size="xs" c="dimmed" mb={4}>Steps ({defs.length})</Text>
                      <Stack gap={4}>
                        {defs.map((d, i) => (
                          <Group key={i} gap="xs">
                            <Text size="xs" c="dimmed" w={16} ta="right">{i + 1}.</Text>
                            <Badge size="xs" color={STEP_TYPE_COLORS[d.type as WorkflowStepType] ?? '#64748B'} variant="light">
                              {STEP_TYPE_LABELS[d.type as WorkflowStepType] ?? d.type}
                            </Badge>
                            <Text size="xs">{d.name}</Text>
                          </Group>
                        ))}
                      </Stack>
                    </Paper>
                  );
                } catch { return null; }
              })()}
              <TextInput
                label="Record Label"
                description="What record is this workflow for?"
                placeholder="e.g., Fulton County -- Road Debris Removal"
                value={startRecordLabel}
                onChange={e => setStartRecordLabel(e.target.value)}
              />
              <TextInput
                label="Record ID"
                description="Optional -- the Rid of the source record"
                placeholder="e.g., 42"
                value={startRecordId}
                onChange={e => setStartRecordId(e.target.value)}
              />
            </>
          )}
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" onClick={() => setStartOpen(false)}>Cancel</Button>
            <Button style={{ backgroundColor: '#7C3AED' }} onClick={handleStartWorkflow} disabled={!selectedTemplate || starting} loading={starting}>
              Start Workflow
            </Button>
          </Group>
          {activeTemplates.length === 0 && (
            <Paper p="md" radius="sm" withBorder>
              <Text size="sm" c="dimmed" ta="center">No active workflow templates found. Create one in the Templates tab first.</Text>
            </Paper>
          )}
        </Stack>
      </Modal>
    </div>
  );
}
